// index.js
import PropTypes from "prop-types";
import { useCallback, useState } from "react";
import { Copy, Check } from "lucide-react";
import { useId } from "react";
import { DEFAULT_ACTION_TEXT, COPIED_TIMEOUT, BACKDROP_KEYS } from "./constants";
import { copyToClipboard } from "./utils";
import "./styles.scss";

function AlertModal({ open, title, children, actionText = DEFAULT_ACTION_TEXT, onAction, messageData }) {
  const [copied, setCopied] = useState(false);
  const titleId = useId();

  const handleCopy = useCallback(async () => {
    const success = await copyToClipboard(messageData);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), COPIED_TIMEOUT);
    }
  }, [messageData]);

  const handleDismiss = useCallback(
    e => {
      if (onAction) onAction(e);
    },
    [onAction]
  );

  const handleBackdropKeyDown = useCallback(
    e => {
      if (BACKDROP_KEYS.includes(e.key)) {
        e.preventDefault();
        handleDismiss(e);
      }
    },
    [handleDismiss]
  );

  const handleBackdropClick = useCallback(
    e => {
      if (e.target === e.currentTarget) handleDismiss(e);
    },
    [handleDismiss]
  );

  if (!open) return null;

  return (
    <div
      className="alert-modal-backdrop"
      role="button"
      tabIndex={0}
      aria-label="Dismiss dialog"
      onClick={handleBackdropClick}
      onKeyDown={handleBackdropKeyDown}
    >
      <div className="alert-modal" role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <div className="alert-modal-header">
          <span id={titleId}>{title}</span>
          <button className="copy-btn" onClick={handleCopy}>
            {copied ? <Check size={18} /> : <Copy size={18} />}
            {copied && <span className={`copy-indicator show`}>Copied!</span>}
          </button>
        </div>
        <div className="alert-modal-body">{children}</div>
        <div className="alert-modal-footer">
          <button className="alert-modal-action" onClick={handleDismiss}>
            {actionText}
          </button>
        </div>
      </div>
    </div>
  );
}

AlertModal.propTypes = {
  open: PropTypes.bool,
  title: PropTypes.string,
  children: PropTypes.node,
  actionText: PropTypes.string,
  onAction: PropTypes.func,
  messageData: PropTypes.string,
};

export default AlertModal;
